import axios, { AxiosError } from "axios";
import { plainToInstance } from "class-transformer";
import { APIKeysResult } from "healthex-common/apikeys.dtos";
import { AWSDataDeliveryInfoDto } from "healthex-common/document.retrieval.dtos";
import { OrganizationUpdateFailureReason } from "healthex-common/organization.dtos";
import { err, voidResult } from "healthex-common/result";
import { asEnum } from "healthex-common/utils";
import { authenticatedApiConfig, generateURL } from "./BaseApiService";
const _INGEST_TYPE_VALUES = ["redox", "epic", "bundle"];
export const INGEST_TYPE_VALUES = [..._INGEST_TYPE_VALUES];
export function asIngestType(input) {
    if (INGEST_TYPE_VALUES.includes(input)) {
        return input;
    }
    else {
        return null;
    }
}
export class ProvisioningService {
    constructor(authenticationProvider, environment) {
        this.authenticationProvider = authenticationProvider;
        this.environment = environment;
    }
    async createOrganization(input) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/organizations"), input, await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getOrganizationById(id) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId", { orgId: id }), await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getOrganizationGoLiveChecklist(id) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/golive/checklist", { orgId: id }), await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getOrganizationPublicProfileById(id) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/public", { orgId: id }), await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getOrganizations(type) {
        let url = generateURL(this.environment.API_BASE_URL, "/organizations");
        if (type) {
            url += `?type=${type}`;
        }
        const response = await axios.get(url, await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getOrganizationPublicProfiles(type) {
        let url = generateURL(this.environment.API_BASE_URL, "/organizations/public");
        if (type) {
            url += `?type=${type}`;
        }
        const response = await axios.get(url, await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getOrganizationPublicProfileByVanitySubdomain(vanitySubdomain) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, `/organizations/public?vanitySubdomain=${vanitySubdomain}`), await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateOrganizationById(id, update) {
        await axios.put(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId", { orgId: id }), update, await authenticatedApiConfig(this.authenticationProvider));
    }
    async updateOrganizationDetails(id, update) {
        var _a;
        try {
            await axios.put(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/details", { orgId: id }), update, await authenticatedApiConfig(this.authenticationProvider));
        }
        catch (e) {
            if (axios.isAxiosError(e) && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.data)) {
                const errorData = e.response.data;
                if (errorData.message) {
                    const failureReason = asEnum(OrganizationUpdateFailureReason)(errorData.message);
                    if (failureReason) {
                        return failureReason;
                    }
                    else {
                        throw e;
                    }
                }
            }
        }
        return null;
    }
    async getOrganizationLogoUploadUrl(id, file) {
        const localFileInfo = { filename: file.name };
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/logoUploadUrl", { orgId: id }), localFileInfo, await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async getUsersByOrganization(orgId, nameOrEmailPrefix, start, end) {
        const urlParams = { orgId };
        const url = generateURL(this.environment.API_BASE_URL, `/organizations/:orgId/users`, urlParams);
        const response = await axios.get(url, await authenticatedApiConfig(this.authenticationProvider, { nameOrEmailPrefix, start, end }));
        return response.data;
    }
    async getUserByOrganizationAndId(orgId, userId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/users/:userId", { orgId, userId }), await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async inviteUser(orgId, user) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/users", { orgId }), user, await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateUser(orgId, userId, user) {
        var _a, _b, _c;
        try {
            await axios.put(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/users/:userId", { orgId, userId }), user, await authenticatedApiConfig(this.authenticationProvider));
            return voidResult();
        }
        catch (e) {
            if (e instanceof AxiosError && ((_a = e.response) === null || _a === void 0 ? void 0 : _a.status) === 401 && ((_b = e.response) === null || _b === void 0 ? void 0 : _b.data.error)) {
                return err((_c = e.response) === null || _c === void 0 ? void 0 : _c.data.error);
            }
            throw e;
        }
    }
    async getUserAvatarUploadUrl(orgId, userId, file) {
        const localFileInfo = { filename: file.name };
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/users/:userId/avatarUploadUrl", {
            orgId,
            userId,
        }), localFileInfo, await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async forcePasswordUpdateForUser(orgId, userId) {
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/users/:userId/$force_password_change", {
            orgId,
            userId,
        }), {}, await authenticatedApiConfig(this.authenticationProvider));
        return response.status;
    }
    async deleteUser(orgId, userId) {
        const response = await axios.delete(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/users/:userId", { orgId, userId }), await authenticatedApiConfig(this.authenticationProvider));
        return response.status;
    }
    async uploadFileToUrl(url, file, onUploadProgress) {
        let config;
        if (onUploadProgress) {
            config = {
                onUploadProgress: (progressEvent) => onUploadProgress(progressEvent.total ? (progressEvent.loaded / progressEvent.total) * 100 : 0),
            };
        }
        return await axios.put(url, file, config);
    }
    async getAllAPIKeys(orgId, start, end) {
        const urlParams = {
            orgId,
        };
        if (start !== undefined)
            urlParams.start = String(start);
        if (end !== undefined)
            urlParams.end = String(end);
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/keys?start=:start&end=:end", urlParams), await authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(APIKeysResult, response.data);
    }
    async createAPIKey(orgId, keyName, permissions) {
        const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/keys", { orgId }), {
            keyName,
            permissions,
        }, await authenticatedApiConfig(this.authenticationProvider));
        return response.data;
    }
    async updateAPIKey(orgId, keyId, keyName, status, permissions) {
        const response = await axios.put(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/keys/:keyId", { orgId, keyId }), {
            keyName,
            status,
            permissions,
        }, await authenticatedApiConfig(this.authenticationProvider));
        return response.status;
    }
    async deleteAPIKey(orgId, keyId) {
        const response = await axios.delete(generateURL(this.environment.API_BASE_URL, "/organizations/:orgId/keys/:keyId", { orgId, keyId }), await authenticatedApiConfig(this.authenticationProvider));
        return response.status;
    }
    async getAWSDataDeliveryInfo() {
        const url = generateURL(this.environment.API_BASE_URL, "/aws-data-delivery-info");
        const response = await axios.get(url, await authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(AWSDataDeliveryInfoDto, response.data);
    }
}
